import React, { useEffect } from 'react';
import type { UseFormRegister, FieldValues, FieldErrors } from 'react-hook-form';
import { getValidationResponse } from '../utils';
import type { CustomField } from '../types';

type CustomControlProps = {
  field: CustomField;
  theme?: 'light' | 'dark';
  /** Registry of custom components that can be rendered */
  customComponents?: Record<string, React.ComponentType<Record<string, unknown>>>;
  register?: UseFormRegister<FieldValues>;
  errors?: FieldErrors<FieldValues>;
  /** Callback when custom field value changes */
  onChange?: (name: string, value: unknown) => void;
};

/**
 * CustomControl - Renders custom React components within the form
 *
 * This control allows rendering of custom components (like BusinessNameSearch)
 * that aren't part of the standard form field types.
 *
 * @example
 * <CustomControl
 *   field={{
 *     id: 'companyName',
 *     fieldType: 'custom',
 *     component: 'BusinessNameSearch',
 *     componentProps: { label: 'Company Name', required: true }
 *   }}
 *   customComponents={{ BusinessNameSearch }}
 *   onChange={(name, value) => console.log(name, value)}
 * />
 */
const CustomControl: React.FC<CustomControlProps> = ({
  field,
  theme,
  customComponents = {},
  register,
  errors,
  onChange,
}) => {
  const Component = customComponents[field.component];

  // Register the custom field with react-hook-form for required validation
  useEffect(() => {
    if (register && field.required) {
      register(field.name, {
        required: field.validationMessages?.required || 'This field is required',
      });
    }
  }, [register, field.name, field.required, field.validationMessages?.required]);

  if (!Component) {
    console.warn(`Custom component "${field.component}" not found in customComponents registry`);
    return null;
  }

  const handleChange = (value: unknown) => {
    if (onChange) {
      onChange(field.name, value);
    }
  };

  const validationResponse = errors
    ? getValidationResponse({ field: field.name, errors })
    : undefined;
  const errorMessage =
    validationResponse?.validationType === 'error' ? validationResponse.message : undefined;

  const props = {
    name: field.name,
    theme,
    ...field.componentProps,
    onSelect: handleChange,
    error: errorMessage,
  } as Record<string, unknown>;

  return <Component {...props} />;
};

export default CustomControl;
